import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import type { HouseholdService, SessionService } from "@/shared/services/types";
import type { HouseholdBrief, HouseholdDetail, SessionState } from "@/shared/contracts";

export type TravelIdentity = { userId: string; petId: string; householdId: string; homeId: string };
export type TravelScope = TravelIdentity & { householdVersion: number };
export type CurrentTravel<T> = { scope: TravelScope; value: T; confirmed: number };
// Local ordering only: never a household version or proof carried by a response.
let confirmation = 0;
const authorityFailures = new Set<(identity: string, error: unknown) => void>();
const rejectAuthority = (identity: string, error: unknown) => authorityFailures.forEach(stop => stop(identity, error));
export const travelScopeKey = (s: TravelScope) => JSON.stringify([s.userId, s.petId, s.householdId, s.homeId, s.householdVersion]);
export const scopeFailure = () => Error("这份旅行资料的当前身份尚未确认，请重新查看。");
export async function confirmTravel(identity: TravelIdentity, services: { session: SessionService; households: HouseholdService }, signal: AbortSignal, current: () => boolean) {
  const active = () => { if (signal.aborted || !current()) throw scopeFailure(); };
  active();
  const [s, h] = await Promise.all([services.session.current(), services.households.detail(identity.householdId, signal)]);
  active();
  if (!s.authenticated || s.user?.user_id !== identity.userId || h.household.household_id !== identity.householdId
      || h.household.home_id !== identity.homeId || !h.household.home_activated
      || !h.household.pets.some(p => p.pet_id === identity.petId && p.join_step === "moved_in")
      || !h.members.some(m => m.user_id === identity.userId) || !Number.isSafeInteger(h.version) || h.version < 0) throw scopeFailure();
  return { scope: { ...identity, householdVersion: h.version }, confirmed: ++confirmation };
}
export function checkTravelResponse(value: unknown, scope: TravelScope, planId?: string) {
  if (value === null && !planId) return;
  if (!value || typeof value !== "object") throw scopeFailure();
  const r = value as Record<string, unknown>;
  // Plan/longing frozen DTOs have no pet_id; bind them to the original request,
  // fresh authority and cancellation fence, not a fabricated response identity.
  if (("pet_id" in r && r.pet_id !== scope.petId) || ("household_id" in r && r.household_id !== scope.householdId)
      || (planId && r.plan_id !== planId)) throw scopeFailure();
}
type Authority = { identity: string; epoch: number; scope?: TravelScope; confirmed?: number; error?: unknown };
/** 缓存里新拿到的会话／家庭数据，和已经确认的身份还对不对得上（只认这几项：本人、这个家、这只宠物、成员、家庭版本）。对不上就要收起。 */
function cacheStillMatches(kind: "session" | "households" | "detail", data: unknown, scope: TravelScope): boolean {
  if (kind === "session") {
    const s = data as SessionState | undefined;
    return Boolean(s?.authenticated && s.user?.user_id === scope.userId);
  }
  if (kind === "households") {
    const list = data as HouseholdBrief[] | undefined;
    return Array.isArray(list) && list.some(h => h.household_id === scope.householdId && h.home_id === scope.homeId && h.pets.some(p => p.pet_id === scope.petId));
  }
  const d = data as HouseholdDetail | undefined;
  return Boolean(d?.household && d.household.household_id === scope.householdId && d.household.home_id === scope.homeId && d.household.home_activated
    && d.household.pets.some(p => p.pet_id === scope.petId && p.join_step === "moved_in") && d.members.some(m => m.user_id === scope.userId) && d.version === scope.householdVersion);
}
export function useTravelScope() {
  const household = useOptionalCurrentHousehold(), services = useServices(), client = useQueryClient();
  const identity: TravelIdentity | null = env.dataMode === "live" && household?.userId && household.pet?.pet_id && household.household?.household_id && household.household.home_id
    ? { userId: household.userId, petId: household.pet.pet_id, householdId: household.household.household_id, homeId: household.household.home_id } : null;
  const key = JSON.stringify(identity);
  const boundary = useMemo(() => ({ alive: false, epoch: 0, controller: new AbortController() }), [key, services.session, services.households, client]);
  const [view, setView] = useState<Authority>({ identity: key, epoch: 0 });
  const confirmedScope = useRef<TravelScope | undefined>(undefined);
  const refresh = useCallback(async () => {
    if (!identity || !boundary.alive) return;
    boundary.controller.abort(); boundary.controller = new AbortController(); const epoch = ++boundary.epoch;
    setView({ identity: key, epoch });
    const current = () => boundary.alive && boundary.epoch === epoch;
    try {
      const result = await confirmTravel(identity, services, boundary.controller.signal, current);
      if (current()) setView({ identity: key, epoch, ...result });
    } catch (error) { if (current()) rejectAuthority(key, error); }
  }, [key, boundary, services.session, services.households]);
  useLayoutEffect(() => {
    boundary.alive = true;
    const reject = (identityKey: string, error: unknown) => {
      if (identityKey !== key) return;
      boundary.epoch++; boundary.controller.abort();
      setView({ identity: key, epoch: boundary.epoch, error });
    };
    authorityFailures.add(reject);
    const keys = [queryKeys.session, queryKeys.households(identity?.userId ?? "-"), queryKeys.householdDetail(identity?.userId ?? "-", identity?.householdId ?? "-")].map(k => JSON.stringify(k));
    const kinds = ["session", "households", "detail"] as const;
    const stop = client.getQueryCache().subscribe(e => {
      const at = keys.indexOf(JSON.stringify(e.query.queryKey));
      if (at < 0 || !["updated", "removed"].includes(e.type)) return;
      if (e.type === "updated") {
        const kind = e.action.type;
        if (kind === "success" && !(e.action as { manual?: boolean }).manual) {
          // 后台拉取完成（不是应用代码 setQueryData 主动写的）：例行重拉回来的和已确认的身份对得上就不动；还没确认下来时也不动（确认自己会按最新的会话和家庭核）
          const scope = confirmedScope.current;
          if (!scope || cacheStillMatches(kinds[at], e.query.state.data, scope)) return;
        } else if (kind !== "success" && kind !== "invalidate" && kind !== "error" && kind !== "setState") return;  // 开始拉、重试中、暂停：数据还没变
      }
      // 显式失效、出错、移除、应用代码主动写缓存（登录、入住后写会话），或者拉回来的数据和已确认的身份对不上：照旧立刻收起，等主人点「重新确认身份」
      rejectAuthority(key, scopeFailure());
      void client.cancelQueries({ predicate: q => q.queryKey[0] === "travel" && q.queryKey[2] === identity?.userId && q.queryKey[3] === identity?.petId && q.queryKey.includes("current-scope") });
    });
    void refresh();
    return () => { boundary.alive = false; boundary.epoch++; boundary.controller.abort(); stop(); authorityFailures.delete(reject); };
  }, [boundary, key, client, refresh]);
  const shown = identity && boundary.alive && view.identity === key && view.epoch === boundary.epoch ? view : null;
  const scope = shown?.scope;
  confirmedScope.current = scope;
  const epoch = boundary.epoch;
  const current = () => Boolean(scope && boundary.alive && boundary.epoch === epoch && !boundary.controller.signal.aborted);
  const verify = async (signal = boundary.controller.signal) => {
    if (!scope || !identity || !current()) throw scopeFailure();
    try {
      const result = await confirmTravel(identity, services, signal, current);
      if (!current() || travelScopeKey(result.scope) !== travelScopeKey(scope)) throw scopeFailure();
      return result;
    } catch (error) {
      // Query cancellation is not loss of authority. A failed fresh authority
      // check is: hide all values from this hook before another render/use.
      if (current() && !signal.aborted) {
        rejectAuthority(key, error);
        void client.cancelQueries({ predicate: q => q.queryKey[0] === "travel" && q.queryKey[2] === identity.userId && q.queryKey[3] === identity.petId && q.queryKey.includes("current-scope") });
      }
      throw error;
    }
  };
  const read = async <T,>(signal: AbortSignal, get: () => Promise<T>, planId?: string): Promise<CurrentTravel<T>> => {
    await verify(signal); const value = await get(); const after = await verify(signal);
    checkTravelResponse(value, after.scope, planId);
    if (!current() || signal.aborted) throw scopeFailure();
    return { ...after, value };
  };
  return { scope, current, verify, read, refresh, canRead: Boolean(identity), error: shown?.error, confirmed: shown?.confirmed ?? Infinity };
}
